import { useState, useEffect, useCallback } from "react";
import { PageContainer } from "@/components/layout/PageContainer";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Truck,
  RefreshCw,
  Plus,
  Search,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Send,
  Download,
  XCircle,
  Eye,
} from "lucide-react";
import { getCurrentShop, getCurrentUser } from "@/lib/auth";
import { getTransfers } from "@/lib/api";
import { NewTransferDialog } from "./ShopInventoryTransfers/NewTransferDialog";
import { TransferDetailDialog } from "./ShopInventoryTransfers/TransferDetailDialog";
import { ReceiveTransferDialog } from "./ShopInventoryTransfers/ReceiveTransferDialog";

const STATUS_CONFIG: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  draft: { label: "Borrador", variant: "secondary" },
  in_transit: { label: "En tránsito", variant: "default" },
  completed: { label: "Completada", variant: "outline" },
  cancelled: { label: "Cancelada", variant: "destructive" },
  issue: { label: "Con incidencia", variant: "destructive" },
};

export default function ShopInventoryTransfers() {
  const currentShop = getCurrentShop()!;
  const currentUser = getCurrentUser();

  const [transfers, setTransfers] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const [direction, setDirection] = useState<"sent" | "received">("sent");
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const limit = 20;

  const [showNew, setShowNew] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [receiveId, setReceiveId] = useState<string | null>(null);

  const loadTransfers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getTransfers(currentShop.id, {
        page,
        limit,
        direction,
        search: search || undefined,
        status: statusFilter || undefined,
      });
      setTransfers(data.transfers);
      setTotal(data.total);
    } catch {
      setTransfers([]);
    } finally {
      setLoading(false);
    }
  }, [currentShop.id, page, direction, search, statusFilter]);

  useEffect(() => {
    loadTransfers();
  }, [loadTransfers]);

  const handleSuccess = () => loadTransfers();

  const totalPages = Math.ceil(total / limit);

  return (
    <PageContainer className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-heading font-bold uppercase flex items-center gap-2">
          <Truck className="h-7 w-7" /> Transferencias
        </h1>
        <p className="text-muted-foreground mt-2">
          Gestiona movimientos de stock entre tiendas — {currentShop.name}
        </p>
      </div>

      {/* Action bar */}
      <div className="flex flex-wrap gap-2 mb-6">
        <Button onClick={() => setShowNew(true)}>
          <Plus className="mr-2 h-4 w-4" /> Nueva Transferencia
        </Button>
        <Button variant="outline" onClick={loadTransfers}>
          <RefreshCw className="mr-2 h-4 w-4" /> Actualizar
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 bg-muted rounded-lg p-1 w-fit">
        <Button
          variant={direction === "sent" ? "default" : "ghost"}
          size="sm"
          onClick={() => { setDirection("sent"); setPage(1); }}
          className="rounded-md"
        >
          <Send className="mr-2 h-4 w-4" /> Enviadas
        </Button>
        <Button
          variant={direction === "received" ? "default" : "ghost"}
          size="sm"
          onClick={() => { setDirection("received"); setPage(1); }}
          className="rounded-md"
        >
          <Download className="mr-2 h-4 w-4" /> Recibidas
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4 items-end">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="pl-9"
            placeholder="Buscar por nº de transferencia..."
          />
        </div>
        <div className="w-[150px]">
          <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
            <SelectTrigger>
              <SelectValue placeholder="Todos los estados" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value=" ">Todos los estados</SelectItem>
              <SelectItem value="draft">Borradores</SelectItem>
              <SelectItem value="in_transit">En tránsito</SelectItem>
              <SelectItem value="completed">Completadas</SelectItem>
              <SelectItem value="cancelled">Canceladas</SelectItem>
              <SelectItem value="issue">Con incidencia</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {(search || statusFilter) && (
          <Button variant="ghost" size="sm" onClick={() => { setSearch(""); setStatusFilter(""); setPage(1); }}>
            ✕ Limpiar
          </Button>
        )}
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="p-3 text-left font-medium">Nº</th>
                  <th className="p-3 text-left font-medium">Fecha</th>
                  <th className="p-3 text-left font-medium">{direction === "sent" ? "Destino" : "Origen"}</th>
                  <th className="p-3 text-right font-medium">Items</th>
                  <th className="p-3 text-right font-medium">Unidades</th>
                  <th className="p-3 text-right font-medium">Valor</th>
                  <th className="p-3 text-center font-medium">Estado</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={8} className="p-8 text-center">
                      <Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
                    </td>
                  </tr>
                ) : transfers.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="p-8 text-center text-muted-foreground">
                      No hay transferencias {direction === "sent" ? "enviadas" : "recibidas"}
                    </td>
                  </tr>
                ) : (
                  transfers.map((t) => {
                    const sc = STATUS_CONFIG[t.status] || { label: t.status, variant: "outline" };
                    const otherShop = direction === "sent"
                      ? (t.shopDestination?.name || t.shopDestinationId)
                      : (t.shopOrigin?.name || t.shopOriginId);
                    return (
                      <tr key={t.id} className="border-b hover:bg-muted/50 cursor-pointer" onClick={() => setDetailId(t.id)}>
                        <td className="p-3 font-mono text-xs">{t.sequential}</td>
                        <td className="p-3 text-muted-foreground">
                          {t.createdAt ? new Date(t.createdAt).toLocaleDateString("es-CU") : "—"}
                        </td>
                        <td className="p-3 text-muted-foreground">{otherShop || "—"}</td>
                        <td className="p-3 text-right">{t.totalItems}</td>
                        <td className="p-3 text-right">{t.totalQuantity}</td>
                        <td className="p-3 text-right font-medium">
                          ${parseFloat(t.totalCost || "0").toFixed(2)}
                        </td>
                        <td className="p-3 text-center">
                          <Badge variant={sc.variant}>{sc.label}</Badge>
                        </td>
                        <td className="p-3 text-right">
                          <div className="flex justify-end gap-1">
                            <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); setDetailId(t.id); }}>
                              <Eye className="h-4 w-4" />
                            </Button>
                            {t.status === "in_transit" && direction === "received" && (
                              <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); setReceiveId(t.id); }}>
                                <Download className="h-4 w-4 text-green-600" />
                              </Button>
                            )}
                            {t.status === "draft" && (
                              <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); setDetailId(t.id); }}>
                                <Send className="h-4 w-4 text-blue-600" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-4">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
            .map((p, idx, arr) => (
              <span key={p} className="flex items-center">
                {idx > 0 && arr[idx - 1] !== p - 1 && <span className="mx-1 text-muted-foreground">...</span>}
                <Button variant={p === page ? "default" : "outline"} size="sm" onClick={() => setPage(p)} className="min-w-[32px]">
                  {p}
                </Button>
              </span>
            ))}
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Dialogs */}
      <NewTransferDialog show={showNew} onClose={() => setShowNew(false)} onSuccess={handleSuccess} />
      <TransferDetailDialog transferId={detailId} show={detailId !== null} onClose={() => setDetailId(null)} onUpdate={handleSuccess} />
      <ReceiveTransferDialog transferId={receiveId} show={receiveId !== null} onClose={() => setReceiveId(null)} onSuccess={handleSuccess} />
    </PageContainer>
  );
}
